import { useEffect, useState } from 'react';

/**
 * Whether the window currently has focus.
 *
 * Seeded from document.hasFocus() rather than from false. The focus and blur
 * events report only *changes*, so a component mounted into a window that is
 * already focused — the normal case — never receives one, and the old initial
 * false meant this stayed false until the member left the tab and came back.
 *
 * It is re-read inside the effect as well as in the initial state, because
 * focus can move between the first render and the listeners being attached.
 *
 * @returns {Boolean} true while the window has focus
 */
const useIsFocused = () => {
    const [isFocused, setIsFocused] = useState(() => document.hasFocus());

    useEffect(() => {
        const onFocus = () => setIsFocused(true);
        const onBlur = () => setIsFocused(false);

        setIsFocused(document.hasFocus());

        window.addEventListener('focus', onFocus);
        window.addEventListener('blur', onBlur);

        return () => {
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('blur', onBlur);
        };
    }, []);

    return isFocused;
};

export default useIsFocused;
